// React hooks
import { useCallback, useEffect, useState } from "react";

// Helpers i API servisi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { OfficesSearchPageCmp, OfficesSearchPageProps } from "./types";
import { OFFICES_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes } from "ui/modules/settings/navigation";
import TabMenu from "ui/modules/settings/pages/tenant/components/TabMenu/TabMenu";

// Lokalne komponente
import OfficesSearchResults from "./components/OfficesSearchResults/OfficesSearchResults";
import OfficesSearchFilters from "./components/OfficesSearchFilters/OfficesSearchFilters";

import { useTranslate } from "helpers";
import {
  OfficeEventType,
  OfficeRow,
  OfficeSearchFormValues,
} from "services/api/offices/types";
import { deleteOfficeAPI, getOfficesAPI } from "services/api/offices/api";
import OfficeEditCard from "./components/OfficeEditCard/OfficeEditCard";
import { initialOfficeSearchForm } from "services/api/offices/consts";
import { Dialog } from "ui/components/Dialog";

const OfficesSearchPage: OfficesSearchPageCmp = (
  props: OfficesSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<OfficeRow>
  >(initialDataWithPagination);

  const [officeId, setOfficeId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [deleteOfficeId, setDeleteOfficeId] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<OfficeSearchFormValues>(
    initialOfficeSearchForm
  );

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getOfficesAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setOfficeId(id);
    setShowWizard(true);
  }, []);

  const handleEventClick = useCallback(async (id: string, name: string) => {
    if (name === OfficeEventType.REMOVE) {
      setDeleteOfficeId(id);
    }
  }, []);

  const handleAddNew = useCallback(() => {
    setOfficeId(null);
    setShowWizard(true);
  }, []);

  const handleSearchChange = useCallback(
    (searchFormValues: OfficeSearchFormValues) => {
      setFilterValues(searchFormValues);

      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination, filterValues.search);
  };

  const handleWizardSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination, filterValues.search);
    setShowWizard(false);
    setOfficeId(null);
  }, [filterValues, searchResults.pagination]);

  const handleWizardClose = useCallback(() => {
    setShowWizard(false);
    setOfficeId(null);
    fetchSearchResult(searchResults.pagination, filterValues.search);
  }, [filterValues, searchResults.pagination]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteOfficeId) return;
    await deleteOfficeAPI(deleteOfficeId, t);
    setDeleteOfficeId(null);
    fetchSearchResult(searchResults.pagination, filterValues.search);
  }, [deleteOfficeId, t, filterValues, searchResults.pagination]);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={OFFICES_SEARCH_PAGE_TITLE.text}
              icon={OFFICES_SEARCH_PAGE_TITLE.icon}
              iconColor={OFFICES_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <OfficesSearchFilters
            formValues={filterValues}
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Dodajte poslovnicu klikom na 'Dodaj poslovnicu'" />
          ) : (
            <OfficesSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onEventClick={handleEventClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>

      <OfficeEditCard
        officeId={officeId}
        show={showWizard}
        onSuccess={handleWizardSuccess}
        onClose={handleWizardClose}
      />

      <Dialog
        open={!!deleteOfficeId}
        title="Brisanje poslovnice"
        message="Jeste li sigurni da želite obrisati ovu poslovnicu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteOfficeId(null)}
      />
    </>
  );
};

OfficesSearchPage.route = Routes.OfficesSearchPageRouter;

export default OfficesSearchPage;
